import React, {useState} from 'react';
import {GitMerge, Mail, RefreshCw, LogOut, CheckCircle2, AlertCircle} from 'lucide-react';
import {Button} from './ui/Button';

interface EmailVerificationRequiredProps {
    email: string;
    onResendEmail: () => Promise<void>;
    onLogout: () => void;
}

export const EmailVerificationRequired: React.FC<EmailVerificationRequiredProps> = ({
    email,
    onResendEmail,
    onLogout,
}) => {
    const [isResending, setIsResending] = useState(false);
    const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleResend = async () => {
        setIsResending(true);
        setResendStatus('idle');
        setErrorMessage('');

        try {
            await onResendEmail();
            setResendStatus('success');
            setTimeout(() => setResendStatus('idle'), 5000);
        } catch (err) {
            setResendStatus('error');
            setErrorMessage(err instanceof Error ? err.message : 'Failed to resend email');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f4f1e6] p-4 text-[#0a0a0a]">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <div className="mb-4 inline-flex items-center gap-3">
                        <div className="nb-shadow-sm rounded-2xl border-2 border-[#0a0a0a] bg-[#22c55e] p-3">
                            <GitMerge size={32} className="text-[#0a0a0a]" strokeWidth={2.6}/>
                        </div>
                        <h1 className="text-4xl font-extrabold tracking-tight">ReviewMe</h1>
                    </div>
                </div>

                <div className="nb-shadow-lg rounded-2xl border-2 border-[#0a0a0a] bg-white p-8">
                    <div className="text-center">
                        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#0a0a0a] bg-[#fde68a] text-[#92600a]">
                            <Mail size={32} strokeWidth={2}/>
                        </div>

                        <h2 className="mb-3 text-2xl font-extrabold">Verify your email</h2>

                        <p className="mb-2 font-medium text-[#4f4f48]">We&apos;ve sent a verification link to:</p>
                        <p className="mb-6 text-lg font-bold">{email}</p>

                        <p className="mb-6 text-sm text-[#4f4f48]">
                            Click the link in your email to verify your account and get full access to ReviewMe.
                        </p>

                        {resendStatus === 'success' && (
                            <div className="mb-4 flex items-center justify-center gap-2 rounded-xl border-2 border-[#0a0a0a] bg-[#bbf7d0] px-4 py-3 font-bold text-[#15803d]">
                                <CheckCircle2 size={20}/>
                                Verification email sent!
                            </div>
                        )}

                        {resendStatus === 'error' && (
                            <div className="mb-4 flex items-center justify-center gap-2 rounded-xl border-2 border-[#0a0a0a] bg-[#fecdd3] px-4 py-3 font-bold text-[#be123c]">
                                <AlertCircle size={20}/>
                                {errorMessage}
                            </div>
                        )}

                        <div className="space-y-3">
                            <Button onClick={handleResend} disabled={isResending} className="w-full text-base">
                                {isResending ? (
                                    <>
                                        <RefreshCw size={20} strokeWidth={2.5} className="animate-spin"/>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Mail size={20} strokeWidth={2.5}/>
                                        Resend verification email
                                    </>
                                )}
                            </Button>

                            <Button variant="secondary" onClick={onLogout} className="w-full text-base">
                                <LogOut size={20} strokeWidth={2.5}/>
                                Log out
                            </Button>
                        </div>
                    </div>
                </div>

                <p className="mt-6 text-center text-sm text-[#9a948a]">
                    Didn&apos;t receive the email? Check your spam folder or try resending.
                </p>
            </div>
        </div>
    );
};
